import { apiService } from "../api.service";

// Requests

// Responses

// Domain Models
import { Subscription, SubscriptionPlan } from "../../models/app/domain";

export class PaymentMethod {
  constructor(data = {}) {
    this.id = data.id;
    this.brand = data.brand;
    this.last4 = data.last4;
    this.expMonth = data.exp_month;
    this.expYear = data.exp_year;
    this.isDefault = data.is_default;
  }

  get expLabel() {
    return `${String(this.expMonth).padStart(2, "0")}/${String(this.expYear).slice(-2)}`;
  }

  get isExpired() {
    const now = new Date();
    return (
      this.expYear < now.getFullYear() ||
      (this.expYear === now.getFullYear() && this.expMonth < now.getMonth() + 1)
    );
  }
}

/**
 * Billing Request
 * @return - RedirectUrlResponse || ErrorResponse
 */
export const billingRequest = async () => {
  const response = await apiService.post(`/payment/sessions/portal`);
  if (response.hasError) {
    return response;
  }
  return { redirectUrl: response.data.redirect_url };
};

/**
 * Fetch Subscription plans Request
 * @return - SubscriptionPlansResponse || ErrorResponse
 */
export const fetchSubscriptionPlansRequest = async () => {
  const response = await apiService.get(`/payment/store/subscription-plans`);
  if (response.hasError) {
    return response;
  }
  return {
    subscriptionPlans: response.data.subscription_plans.map(
      plan => new SubscriptionPlan(plan)
    )
  };
};

/**
 * Fetch Active Subscription Request
 * @return - ActiveSubscriptionResponse || ErrorResponse
 */
export const fetchActiveSubscriptionRequest = async () => {
  const response = await apiService.get(`/businesses/subscription`);
  if (response.hasError) {
    return response;
  }
  return {
    activeSubscription: response?.data?.subscription
      ? new Subscription(response?.data?.subscription)
      : undefined
  };
};

/**
 * Subscribe to plan Request (in-app Elements flow)
 * @return - SubscribeResponse || ErrorResponse
 */
export const subscribeToPlanRequest = async (
  subscriptionPlan,
  paymentMethodId
) => {
  const payload = { subscription_plan_id: Number(subscriptionPlan.id) };
  // Only the new-flow POST sends a payment method; PUT (change plan) must not.
  if (paymentMethodId) {
    payload.payment_method_id = paymentMethodId;
  }
  const response = await apiService.post(`/payment/subscriptions`, payload);
  if (response.hasError) {
    return response;
  }
  return {
    clientSecret: response?.data?.client_secret,
    // 'payment' -> stripe.confirmCardPayment, 'setup' -> stripe.confirmCardSetup
    clientSecretType: response?.data?.client_secret_type
  };
};

/**
 * Fetch saved payment methods Request
 * @return - PaymentMethodsResponse || ErrorResponse
 */
export const fetchPaymentMethodsRequest = async () => {
  const response = await apiService.get(`/payment/payment-methods`);
  if (response.hasError) {
    return response;
  }
  return {
    paymentMethods: (response?.data?.payment_methods || []).map(
      pm => new PaymentMethod(pm)
    )
  };
};

/**
 * Create a SetupIntent for saving a new card Request
 * @return - SetupIntentResponse || ErrorResponse
 */
export const createSetupIntentRequest = async () => {
  const response = await apiService.post(
    `/payment/payment-methods/setup-intent`
  );
  if (response.hasError) {
    return response;
  }
  return { clientSecret: response?.data?.client_secret };
};

/**
 * Set default payment method Request
 * @return - MessageResponse || ErrorResponse
 */
export const setDefaultPaymentMethodRequest = async paymentMethodId => {
  const response = await apiService.patch(
    `/payment/payment-methods/${paymentMethodId}/default`
  );
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Delete payment method Request
 * @return - MessageResponse || ErrorResponse
 */
export const deletePaymentMethodRequest = async paymentMethodId => {
  const response = await apiService.delete(
    `/payment/payment-methods/${paymentMethodId}`
  );
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Cancel subscription Request
 * @return - MessageResponse || ErrorResponse
 */
export const cancelSubscribeRequest = async () => {
  const response = await apiService.delete(`/payment/subscriptions`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Resume subscription Request
 * @return - MessageResponse || ErrorResponse
 */
export const resumeSubscribeRequest = async () => {
  const response = await apiService.post(`/payment/subscriptions/resume`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Change subscription plan Request
 * @return - MessageResponse || ErrorResponse
 */
export const changeSubscriptionPlanRequest = async subscriptionPlan => {
  const payload = { subscription_plan_id: Number(subscriptionPlan.id) };
  const response = await apiService.put(`/payment/subscriptions`, payload);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};
